/**
 * Formes des réponses de l'API v1 — contrat entre l'API et la PWA.
 * Les montants sont en euros (nombres bruts) ; le formatage est fait côté PWA.
 * Les dates sont des chaînes ISO 8601.
 */
import type { Permission, Role } from "./permissions";
import type { TaskStatusKey } from "./labels";

export type ShopType = "INTEGRATED" | "FRANCHISE";
export type PeriodKey = "jour" | "semaine" | "mois" | "annee";
export type CompareKey = "j7" | "m1" | "n1";

// --- Session / contexte ---

export interface BoutiqueRefDTO {
  id: string;
  name: string;
  city: string;
  type: ShopType;
  /** Vide pour une boutique alimentée par la caisse Easy Shop. */
  hiboutikAccountId: string | null;
  /** D'où viennent ses chiffres : synchro Hiboutik ou caisse Easy Shop. */
  salesSource: SalesSourceKey;
  active: boolean;
}

export type SalesSourceKey = "HIBOUTIK" | "EASYSHOP";

export interface MeDTO {
  user: { id: string; name: string; email: string; image: string | null };
  /**
   * Réseaux accessibles. `viaPlatform` : accès par le niveau plateforme (non
   * membre). `logoUrl` : null sans logo (l'interface affiche les initiales).
   */
  organizations: { id: string; name: string; logoUrl: string | null; role: Role; viaPlatform?: boolean }[];
  /** Administrateur plateforme : au-dessus de tous les réseaux. */
  platformAdmin: boolean;
  /** Validation du compte par un administrateur (tant que PENDING, aucun accès). */
  approval: "PENDING" | "APPROVED" | "REJECTED";
  /** Administrateurs plateforme : comptes en attente de validation. */
  pendingApprovals: number;
  organization: { id: string; name: string; slug: string; logoUrl: string | null; itEnabled: boolean } | null;
  role: Role | null;
  permissions: Permission[];
  networkWide: boolean;
  /** Boutiques visibles (périmètre), dans l'ordre d'affichage. */
  boutiques: BoutiqueRefDTO[];
  /** Nombre total de boutiques du réseau (pour « Réseau · N boutiques »). */
  networkSize: number;
}

// --- Tableau de bord ---

export interface PeriodInfoDTO {
  /** "custom" : plage libre (`from`/`to`). */
  key: PeriodKey | "custom";
  cmp: CompareKey;
  title: string; // ex. « aujourd'hui »
  dateLabel: string;
  cmpLabel: string; // ex. « vs J-7 »
}

export interface ChartDTO {
  axis: string[];
  current: number[];
  previous: number[];
  labels: string[];
}

/** Une référence chiffrée (« vs M-1 », « vs N-1 ») d'un KPI : valeur à l'époque et écart. */
export interface CmpCellDTO {
  value: string;
  delta: string;
  up: boolean;
  /** Faux si la référence est indisponible (période non synchronisée, pas d'objectif). */
  hasData: boolean;
}

/** Une colonne de comparaison (période précédente réelle ou même période N-1), tous KPI confondus. */
export interface CmpColumnDTO {
  id: "prev" | "n1";
  /** En-tête : « vs M-1 », « vs S-1 », « vs N-1 ». */
  head: string;
  /** Période visée en toutes lettres (« Juin 2026 »). */
  title: string;
  ca: CmpCellDTO;
  tickets: CmpCellDTO;
  panier: CmpCellDTO;
  marge: CmpCellDTO;
  objectif: CmpCellDTO;
}

export interface BoutiqueCardDTO {
  id: string;
  name: string;
  city: string;
  type: ShopType;
  ca: number;
  /** Variation (%) vs comparaison ; null si pas de référence ou boutique non synchronisée. */
  delta: number | null;
  /** CA de la période de comparaison (à heure égale) ; null comme `delta` si pas de référence. */
  prevCa: number | null;
  tickets: number;
  panier: number;
  marge: number | null;
  objectivePct: number | null;
  rank: number;
  openTasks: number;
  lateTasks: number;
  unread: number;
  /** false si aucune synchro réussie depuis 48 h (carte grisée « Non synchronisée »). */
  synced: boolean;
  lastSyncAt: string | null;
  healthScore: number;
  /** Promos (campagnes) en cours aujourd'hui dans la boutique. */
  promosToday: { id: string; name: string }[];
}

export interface ProductRankDTO {
  /** Identifiant interne du produit (produit réseau) ; null tant qu'il n'est pas rattaché. */
  id: string | null;
  name: string;
  quantity: number;
  revenue: number;
  /** CA de la période comparée (à heure égale) ; null sans référence. */
  prevRevenue: number | null;
}

/**
 * Ventes par heure d'une journée (vue Jour) : 24 valeurs, CA TTC par heure.
 * Les heures à venir de la journée en cours valent null.
 */
export interface HourlyDTO {
  /** Toutes les boutiques du périmètre. */
  current: (number | null)[];
  /** Succursales intégrées / franchises du périmètre. */
  integrated: (number | null)[];
  franchise: (number | null)[];
  /** Journée comparée, entière (on y voit la suite de la journée de référence). */
  previous: number[];
  /** Heure en cours (0–23) si la journée n'est pas finie, sinon null. */
  nowHour: number | null;
}

export interface DashboardDTO {
  period: PeriodInfoDTO;
  lastSyncAt: string | null;
  alerts: {
    tasksLate: number;
    boutiquesDown: number;
    ruptures: number;
    /** Boutiques en forte baisse (pour filtrer la grille). */
    boutiquesDownIds: string[];
    /** Boutiques en hausse vs la comparaison. */
    boutiquesUpIds: string[];
    /** Boutiques ayant atteint leur objectif de la période. */
    objectiveReachedIds: string[];
    /** Boutiques ayant au moins un produit en rupture. */
    ruptureBoutiqueIds: string[];
  };
  kpis: {
    ca: number;
    delta: number | null;
    /** CA de la période comparée (à heure égale) ; null sans référence. */
    prevCa: number | null;
    tickets: number;
    panier: number;
    marge: number | null;
    objectivePct: number | null;
    caIntegrated: number;
    caFranchise: number;
  };
  /** Comparaisons M-1 / N-1 de chaque KPI (réseau du périmètre). */
  cmpColumns: CmpColumnDTO[];
  /** Mêmes comparaisons, restreintes aux succursales / franchises (CA). */
  cmpColumnsIntegrated: CmpColumnDTO[];
  cmpColumnsFranchise: CmpColumnDTO[];
  chart: ChartDTO;
  /** Ventes par heure ; null hors vue d'une journée. */
  hourly: HourlyDTO | null;
  summary: { text: string; ai: boolean; events: { time: string; text: string }[] };
  topIntegrated: BoutiqueCardDTO[];
  topFranchise: BoutiqueCardDTO[];
  topProducts: ProductRankDTO[];
  boutiques: BoutiqueCardDTO[];
  demo: boolean;
}

export interface PositionIndicatorDTO {
  label: string;
  value: number | null;
  unit: "eur" | "pct";
  /** Largeur de la barre (0–100) et position du repère moyenne réseau (0–100). */
  pct: number;
  avgPct: number;
}

export interface BoutiqueDetailDTO {
  boutique: BoutiqueCardDTO & { openingHours: string | null; hiboutikAccountId: string | null };
  period: PeriodInfoDTO;
  networkSize: number;
  network: { panier: number; marge: number | null; objectivePct: number | null };
  /** Comparaisons M-1 / N-1 de chaque KPI de la boutique. */
  cmpColumns: CmpColumnDTO[];
  chart: ChartDTO;
  /** Ventes par heure ; null hors vue d'une journée. */
  hourly: HourlyDTO | null;
  topProducts: ProductRankDTO[];
  position: PositionIndicatorDTO[];
  summary: string;
  staffMessage: { text: string; isOverride: boolean; date: string } | null;
}

// --- Ventes ---

export interface SaleLineDTO {
  name: string;
  quantity: number;
  /** Prix unitaire avant remise (€). */
  unitPrice: number;
  /** Remise de la ligne (€), 0 si aucune. */
  discount: number;
  /** Remise de la ligne en % du prix avant remise, null si aucune. */
  discountPct: number | null;
  /** Montant encaissé pour la ligne, remise déduite (€). */
  amount: number;
  /** Prix d'achat unitaire (€), null si inconnu. */
  unitCost: number | null;
}

export interface SaleDTO {
  id: string;
  at: string;
  time: string;
  items: number;
  amount: number;
  discount: number;
  /** Bénéfice de la vente, null si aucun prix d'achat connu. */
  profit: number | null;
  customer: string | null;
  customerContact: string | null;
  payment: string | null;
  lines: SaleLineDTO[];
}

export interface SalesDTO {
  sales: SaleDTO[];
  page: number;
  pages: number;
  total: number;
  /** Totaux de la période (toutes pages, filtre de recherche appliqué). */
  totals: { amount: number; discount: number; discountedSales: number; profit: number | null };
}

// --- Stock ---

export type StockStatusKey = "rupture" | "surveiller" | "ok";

export interface StockRowDTO {
  /** Identifiant interne du produit (produit réseau) ; null tant qu'il n'est pas rattaché. */
  id: string | null;
  /** product_id dans la caisse de la boutique (propre à la boutique). */
  productId: number;
  name: string;
  category: string | null;
  price: number;
  /** Prix d'achat (€), null si inconnu. */
  supplyPrice: number | null;
  stock: number;
  soldWindow: number;
  /** Quantité vendue sur les 30 derniers jours (aujourd'hui inclus). */
  sold30: number;
  /** Quantité vendue sur tout l'historique synchronisé (voir `salesSince`). */
  soldTotal: number;
  coverageDays: number | null;
  lastRestockAt: string | null;
  lastSoldAt: string | null;
  status: StockStatusKey;
  promo: { label: string; pct: number } | null;
}

/** Indicateurs de valeur du stock (en stock positif uniquement). */
export interface StockValueKpisDTO {
  /** Références en stock. */
  refs: number;
  /** Articles (unités) en stock. */
  units: number;
  /** Valeur au prix d'achat (€) — produits au prix d'achat connu. */
  costValue: number;
  /** Valeur au prix de vente TTC (€). */
  retailValue: number;
  /** Marge potentielle (€) : valeur de vente − valeur d'achat, sur les produits au prix d'achat connu. */
  margin: number | null;
  /** Marge potentielle en % de la valeur de vente (mêmes produits). */
  marginPct: number | null;
  /** Unités dont le prix d'achat est inconnu (exclues de la valeur d'achat et de la marge). */
  unitsWithoutCost: number;
}

export interface BoutiqueStockDTO {
  updatedAt: string | null;
  windowDays: number;
  /** Premier mois de l'historique de ventes (« Vendu total » depuis), ISO. */
  salesSince: string | null;
  kpis: StockValueKpisDTO;
  products: StockRowDTO[];
}

/** Ligne de la vue consolidée « Stocks & remises » (produit tous périmètres confondus). */
export interface NetworkStockRowDTO {
  /** Clé de la ligne : l'identifiant interne, ou le nom pour un produit pas encore rattaché. */
  key: string;
  /** Identifiant interne du produit (produit réseau) ; null tant qu'il n'est pas rattaché. */
  id: string | null;
  name: string;
  category: string | null;
  /** Prix de vente le plus courant du périmètre. */
  price: number;
  /** Prix d'achat le plus courant, null si inconnu. */
  supplyPrice: number | null;
  stock: number;
  stockValue: number;
  soldWindow: number;
  sold30: number;
  soldTotal: number;
  /** Dernier réassort dans une des boutiques. */
  lastRestockAt: string | null;
  coverageDays: number | null;
  firstSeenAt: string | null; // « Ajouté »
  lastSoldAt: string | null;
  status: StockStatusKey;
  promo: { label: string; pct: number } | null;
  boutiquesRupture: number;
}

export interface NetworkStockDTO {
  updatedAt: string | null;
  windowDays: number;
  salesSince: string | null;
  refs: number;
  stockValue: number;
  kpis: StockValueKpisDTO;
  toPromo: number;
  slowMoving: number;
  rows: NetworkStockRowDTO[];
}

/** Dernier arrivage d'un produit (réassort). */
export interface ProductArrivalDTO {
  boutiqueId: string;
  city: string;
  date: string | null;
  quantity: number;
  unitCost: number | null;
  supplier: string | null;
}

/** Chiffres d'un produit dans une boutique (fiche ouverte depuis une boutique). */
export interface ProductFocusDTO {
  boutiqueId: string;
  city: string;
  found: boolean;
  price: number | null;
  supplyPrice: number | null;
  unitMargin: number | null;
  marginPct: number | null;
  stock: number | null;
  soldWindow: number;
  coverageDays: number | null;
  stockCostValue: number | null;
  stockRetailValue: number;
  lastRestockAt: string | null;
  lastSoldAt: string | null;
  status: StockStatusKey | null;
  promo: { label: string; pct: number } | null;
}

export interface ProductSheetDTO {
  /** Identifiant interne du produit (produit réseau). */
  id: string;
  /** Nom réseau : le plus répandu parmi les boutiques. */
  name: string;
  category: string | null;
  subCategory: string | null;
  /** Prix de vente TTC (€) le plus courant du périmètre. */
  price: number | null;
  /** Écart de prix de vente entre boutiques du périmètre (null si prix unique). */
  priceRange: { min: number; max: number } | null;
  /** Prix d'achat (€) le plus courant, null si inconnu. */
  supplyPrice: number | null;
  /** Marge unitaire (€) et taux (% du prix de vente TTC), null si le prix d'achat est inconnu. */
  unitMargin: number | null;
  marginPct: number | null;
  status: StockStatusKey | null;
  promo: { label: string; pct: number } | null;
  stockTotal: number;
  /** Valeur du stock au prix d'achat (null si inconnu) et au prix de vente (€). */
  stockCostValue: number | null;
  stockRetailValue: number;
  soldTotal: number;
  /** Fenêtre de ventes (jours) des quantités vendues. */
  windowDays: number;
  coverageDays: number | null;
  lastSoldAt: string | null;
  /** Dernier réassort constaté dans le périmètre. */
  lastRestockAt: string | null;
  /** Derniers arrivages de ce produit (plus récent d'abord). */
  arrivals: ProductArrivalDTO[];
  boutiquesRupture: number;
  stockByBoutique: { boutiqueId: string; city: string; type: ShopType; stock: number | null }[];
  salesByBoutique: { boutiqueId: string; city: string; type: ShopType; quantity: number }[];
  /** Détail de la boutique d'où la fiche est ouverte, si demandé. */
  focus: ProductFocusDTO | null;
  /** CA (€ TTC) du produit sur la fenêtre `windowDays`, périmètre de l'utilisateur. */
  revenueWindow: number;
  /** Quantités vendues par semaine ISO (12 dernières, la plus ancienne d'abord), périmètre de l'utilisateur. */
  weeklySales: { week: number; from: string; quantity: number }[];
  /** Une ligne par boutique du périmètre (ventes et CA sur `windowDays`). */
  boutiques: {
    boutiqueId: string;
    city: string;
    type: ShopType;
    /** Nom du produit dans la caisse de cette boutique (null s'il n'y est pas rattaché). */
    localName: string | null;
    found: boolean;
    stock: number | null;
    sold: number;
    revenue: number;
    coverageDays: number | null;
  }[];
  /**
   * Moyenne par boutique sur tout le réseau, anonymisée (aucune boutique citée).
   * Renseignée seulement pour un utilisateur qui ne voit pas tout le réseau.
   */
  networkAverage: { sold: number; stock: number; revenue: number; coverageDays: number | null } | null;
  /** Promos / campagnes du périmètre qui portent sur ce produit (récentes et à venir). */
  promos: { id: string; name: string; discountPct: number | null; status: "ACTIVE" | "PLANNED" | "DONE"; startDate: string; endDate: string }[];
}

// --- Arrivages ---

export interface ArrivalDTO {
  id: string;
  boutiqueId: string;
  city: string;
  date: string | null;
  supplier: string | null;
  units: number;
  amount: number;
  /** `id` : identifiant interne du produit (null tant qu'il n'est pas rattaché). */
  lines: { id: string | null; name: string; quantity: number; unitCost: number | null }[];
}

export interface ArrivalsDTO {
  total: number;
  units: number;
  suppliers: { name: string; amount: number; pct: number }[];
  average: number;
  months: { key: string; label: string; total: number; items: ArrivalDTO[] }[];
}

// --- Messagerie ---

export interface AttachmentDTO {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  url: string;
}

export interface TaskAssignmentDTO {
  id: string;
  taskId: string;
  boutiqueId: string;
  status: TaskStatusKey;
  late: boolean;
  seenAt: string | null;
  doneAt: string | null;
  validatedAt: string | null;
  refusalReason: string | null;
  proofs: AttachmentDTO[];
  task: {
    id: string;
    title: string;
    description: string | null;
    type: "PROMO" | "POSTER" | "DISPLAY" | "RESTOCK" | "ARRIVAL" | "FREE";
    priority: "LOW" | "NORMAL" | "HIGH";
    dueAt: string | null;
    requiresProof: boolean;
    /** Une facture doit être jointe avant de marquer la tâche faite. */
    requiresInvoice: boolean;
    /** Créateur de la tâche : il peut la valider, la refuser ou l'invalider. */
    createdById: string | null;
    attachments: AttachmentDTO[];
  };
}

export interface MessageDTO {
  id: string;
  conversationId: string;
  kind: "TEXT" | "TASK" | "ANNOUNCEMENT";
  side: "NETWORK" | "BOUTIQUE";
  author: { id: string; name: string } | null;
  /** Libellé affiché : « Tête de réseau » ou la ville de la boutique. */
  authorLabel: string;
  body: string;
  createdAt: string;
  mine: boolean;
  attachments: AttachmentDTO[];
  assignment: TaskAssignmentDTO | null;
  announcement: { id: string; title: string; seenBy: number; recipients: number; seen: boolean } | null;
}

export interface ConversationDTO {
  id: string;
  boutique: { id: string; name: string; city: string; type: ShopType };
  lastMessageAt: string | null;
  lastMessage: string | null;
  unread: number;
  openTasks: number;
  lateTasks: number;
}

export interface MessagesPageDTO {
  messages: MessageDTO[];
  hasMore: boolean;
}

export interface TaskTrackingDTO {
  id: string;
  title: string;
  type: TaskAssignmentDTO["task"]["type"];
  priority: TaskAssignmentDTO["task"]["priority"];
  dueAt: string | null;
  createdAt: string;
  executionRate: number;
  rows: {
    assignmentId: string;
    boutiqueId: string;
    city: string;
    type: ShopType;
    status: TaskStatusKey;
    late: boolean;
    seenAt: string | null;
    doneAt: string | null;
  }[];
}

// --- Campagnes ---

export interface CampaignDTO {
  id: string;
  name: string;
  description: string | null;
  /** Remise indicative — ancienne donnée ; les nouvelles campagnes décrivent l'offre dans la description. */
  discountPct: number | null;
  startsAt: string;
  endsAt: string;
  status: "PLANNED" | "ACTIVE" | "DONE";
  productNames: string[];
  boutiques: { id: string; city: string }[];
  /** Fichiers joints (visuels, affiches, PDF…), dans l'ordre d'ajout. */
  attachments: AttachmentDTO[];
  tasksCount: number;
  /** Campagne de la tête de réseau, ou d'une boutique pour elle-même. */
  origin: "NETWORK" | "BOUTIQUE";
  createdBy: string | null;
  /** L'utilisateur courant peut la modifier / supprimer. */
  canEdit: boolean;
}

export interface CampaignImpactDTO {
  before: number;
  during: number;
  after: number;
}

// --- Accès ---

export interface HiboutikAccountDTO {
  id: string;
  label: string;
  subdomain: string;
  apiUser: string;
  apiKeyPreview: string;
  lastConnectionOk: boolean | null;
  lastConnectionAt: string | null;
  boutiques: { id: string; city: string; name: string }[];
}

export interface BoutiqueAdminDTO extends BoutiqueRefDTO {
  warehouseId: number;
  shopId: number | null;
  openingHours: string | null;
  reportEmails: string | null;
  monthlyRevenueTarget: number | null;
  position: number;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  lastConnectionOk: boolean | null;
  /** Effectif minimum souhaité (alerte sous-effectif du planning). */
  minStaff: number;
  /** Boutique de la caisse Easy Shop qui alimente celle-ci (salesSource EASYSHOP). */
  easyshopTenantId: string | null;
}

export interface MemberDTO {
  id: string;
  user: { id: string; name: string; email: string; image: string | null };
  role: Role;
  grantedPermissions: Permission[];
  revokedPermissions: Permission[];
  permissions: Permission[];
  hiboutikAccountIds: string[];
  boutiqueIds: string[];
  /** Nombre de boutiques effectivement visibles. */
  boutiquesVisible: number;
  joinedAt: string;
}

export interface InvitationDTO {
  id: string;
  email: string;
  role: Role;
  status: "PENDING" | "ACCEPTED" | "REVOKED" | "EXPIRED";
  hiboutikAccountIds: string[];
  boutiqueIds: string[];
  expiresAt: string;
  createdAt: string;
  invitedBy: string;
  link?: string;
}

export interface GroupDTO {
  id: string;
  name: string;
  boutiqueIds: string[];
}

// --- Notifications ---

export interface NotificationDTO {
  id: string;
  kind: string;
  severity: "INFO" | "SUCCESS" | "WARNING" | "DANGER";
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface SyncStatusDTO {
  lastSyncAt: string | null;
  boutiques: { id: string; city: string; lastSyncAt: string | null; lastSyncError: string | null }[];
}

// --- Plateforme (tous les réseaux) ---

export interface PlatformNetworkDTO {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  boutiques: number;
  members: number;
  owners: string[];
  ca: number;
  delta: number | null;
  lateTasks: number;
  lastSyncAt: string | null;
  /** Membre du réseau (sinon accès via le niveau plateforme). */
  member: boolean;
}

export interface PlatformOverviewDTO {
  period: PeriodInfoDTO;
  totals: { networks: number; boutiques: number; ca: number; delta: number | null };
  networks: PlatformNetworkDTO[];
}

export interface PlatformAdminDTO {
  id: string;
  name: string;
  email: string;
}

export interface PlatformUserDTO {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  approval: "PENDING" | "APPROVED" | "REJECTED";
  approvedAt: string | null;
  /** Méthodes de connexion (credential, apple). */
  providers: string[];
  networks: string[];
  /** Invitations en attente pour cet e-mail (réseau · rôle). */
  pendingInvitations: string[];
}

// --- Planning des équipes ---

export interface StaffMemberDTO {
  id: string;
  boutiqueId: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  contractHours: number | null;
  color: string | null;
  active: boolean;
  /** Compte lié (la personne voit ses horaires dans l'app). */
  linkedUser: { id: string; name: string } | null;
  /** Fiche importée de la caisse (HIBOUTIK | EASYSHOP), sinon null. */
  externalSource: string | null;
}

/** Une personne de la caisse qui peut entrer dans l'équipe d'une boutique. */
export interface StaffImportCandidateDTO {
  externalSource: "HIBOUTIK" | "EASYSHOP";
  externalId: string;
  name: string;
  email: string | null;
  /** Tickets encaissés dans cette boutique sur les 90 derniers jours (Hiboutik). */
  tickets: number | null;
  actif: boolean;
  /** Déjà dans l'équipe de cette boutique. */
  dejaImporte: boolean;
}

export interface ShiftDTO {
  id: string;
  boutiqueId: string;
  staffMemberId: string;
  /** Jour AAAA-MM-JJ et heures HH:MM (heure de Paris). */
  date: string;
  start: string;
  end: string;
  breakMinutes: number;
  /** Durée travaillée en heures (pause déduite). */
  hours: number;
  note: string | null;
}

export interface AbsenceDTO {
  id: string;
  staffMemberId: string;
  type: "LEAVE" | "SICK" | "TRAINING" | "OTHER";
  startDate: string;
  endDate: string;
  note: string | null;
}

/** Promo visible dans le planning (bandeau des jours, liste « Prochaines promos »). */
export interface PlanningPromoDTO {
  id: string;
  name: string;
  description: string | null;
  discountPct: number | null;
  startDate: string;
  endDate: string;
  status: "PLANNED" | "ACTIVE" | "DONE";
  productNames: string[];
  boutiqueIds: string[];
}

export interface CoverageDTO {
  boutiqueId: string;
  date: string;
  /** Personnes planifiées ce jour-là. */
  staff: number;
  hours: number;
  minStaff: number;
  /** Personnes absentes (rattachées à la boutique). */
  absent: number;
  understaffed: boolean;
}

export interface PlanningDTO {
  from: string;
  days: string[];
  boutiques: { id: string; city: string; type: ShopType; minStaff: number; openingHours: string | null }[];
  staff: StaffMemberDTO[];
  shifts: ShiftDTO[];
  absences: AbsenceDTO[];
  coverage: CoverageDTO[];
  /** Promos actives ou à venir sur la période affichée. */
  promos: PlanningPromoDTO[];
  /** Prochaines promos (30 jours à partir d'aujourd'hui) pour ces boutiques. */
  upcomingPromos: PlanningPromoDTO[];
  canManage: boolean;
}

/** Vue personnelle : mes créneaux à venir et les promos de mes boutiques. */
export interface MyPlanningDTO {
  staffMemberIds: string[];
  shifts: (ShiftDTO & { city: string })[];
  absences: AbsenceDTO[];
  upcomingPromos: PlanningPromoDTO[];
}

// --- Analyse des promos ---

/** Effet mesuré d'une campagne : jours de promo comparés aux mêmes jours de semaine sans promo. */
export interface PromoCampaignResultDTO {
  id: string;
  name: string;
  origin: "NETWORK" | "BOUTIQUE";
  startDate: string;
  endDate: string;
  /** Jours de la campagne (calendrier). */
  days: number;
  /** Jours-boutique mesurés (jour passé, avec une référence comparable). */
  measuredDays: number;
  boutiques: number;
  /** CA réel des jours mesurés et CA « attendu » sans promo (€). */
  revenue: number;
  baseline: number;
  /** Écart de CA vs attendu (%), null si rien de mesurable. */
  upliftPct: number | null;
  ticketsUpliftPct: number | null;
  basketUpliftPct: number | null;
  /** Écart de CA sur les 7 jours qui suivent la fin (%), null si pas encore mesurable. */
  afterUpliftPct: number | null;
  status: "PLANNED" | "ACTIVE" | "DONE";
}

export interface PromoWeekdayDTO {
  /** 0 = lundi … 6 = dimanche. */
  weekday: number;
  label: string;
  /** Activité habituelle du jour (sans promo) vs la moyenne des jours ouverts (100 = moyenne). */
  activityIndex: number | null;
  /** Jours-boutique de promo mesurés ce jour de semaine. */
  promoDays: number;
  upliftPct: number | null;
}

export interface PromoMonthDTO {
  month: number;
  label: string;
  /** Saisonnalité : CA journalier moyen du mois (sans promo) vs la moyenne annuelle (100 = moyenne). */
  seasonIndex: number | null;
  promoDays: number;
  upliftPct: number | null;
}

export interface PromoGapDTO {
  label: string;
  /** Campagnes-boutique dans cette tranche d'écart avec la promo précédente. */
  count: number;
  upliftPct: number | null;
}

export interface PromoInsightsDTO {
  /** Début de l'historique de ventes analysé. */
  since: string | null;
  boutiques: number;
  campaigns: PromoCampaignResultDTO[];
  byWeekday: PromoWeekdayDTO[];
  byMonth: PromoMonthDTO[];
  byGap: PromoGapDTO[];
  /** Part des jours-boutique en promo sur les 90 derniers jours (%). */
  promoShare90: number;
  /** Écart moyen de CA la semaine qui suit une promo (%), null si non mesurable. */
  afterEffectPct: number | null;
  /** Écart moyen de CA pendant les promos (%), toutes campagnes mesurées. */
  overallUpliftPct: number | null;
  recommendations: { tone: "positive" | "warn" | "info"; text: string }[];
}

// --- Administration plateforme : caisse Easy Shop et initialisation ---------

/** État de la connexion à l'API machine de la caisse Easy Shop. */
export interface AdminEasyshopStatusDTO {
  configured: boolean;
  reachable: boolean;
  url: string | null;
  error: string | null;
  /** Boutiques du tableau de bord alimentées par la caisse. */
  linkedBoutiques: number;
}

/** Un compte Hiboutik, tous réseaux confondus (jamais la clé). */
export interface AdminHiboutikAccountDTO {
  id: string;
  label: string;
  subdomain: string;
  apiUser: string;
  lastConnectionOk: boolean | null;
  organization: { id: string; name: string };
  boutiques: { id: string; name: string; city: string; shopId: number | null; salesSource: SalesSourceKey }[];
}

export interface AdminInitialisationOptionsDTO {
  siren?: string | null;
  magasins?: number[] | null;
  depuis?: string | null;
  ouvrirFiches?: boolean;
  moisActifs?: number;
  /** Produits arrivés en stock sur ces N mois (ou encore en stock) ; 0 = tous. */
  moisArrivages?: number;
  joursVendeurs?: number;
}

export interface AdminInitialisationFileDTO {
  nom: string;
  entetes: string[];
  lignes: Record<string, string>[];
  csv: string;
}

/** Une initialisation à blanc, telle que la caisse la rapporte. */
export interface AdminInitialisationDTO {
  id: string;
  compte: string;
  statut: "EN_COURS" | "TERMINE" | "ECHEC";
  creeLe: string;
  termineLe: string | null;
  journal: string[];
  progression: string | null;
  erreur: string | null;
  /** Un magasin (de plus) peut-il être écrit dans la caisse maintenant ? */
  ecriturePossible: boolean;
  /** Identifiant API du compte Hiboutik : propriétaire proposé par défaut. */
  utilisateurApi: string;
  /** La plus récente des écritures. */
  ecriture: AdminEcritureDTO | null;
  /**
   * Toutes les écritures faites depuis cette lecture, dans l'ordre : une lecture
   * sert tous les magasins du compte, l'un après l'autre. Absent d'une caisse
   * antérieure à l'enchaînement (une seule écriture par lecture).
   */
  ecritures?: AdminEcritureDTO[];
  /** Les magasins de la lecture qui restent à écrire. Absent d'une caisse antérieure à l'enchaînement. */
  magasinsAEcrire?: number[];
  /** Pourquoi le magasin suivant doit attendre (écriture ou soldes en cours), s'il le doit. */
  attente?: string | null;
  rapport: {
    compte: string;
    magasins: { id: number; nom: string }[];
    resume: string;
    totaux: Record<string, number>;
    anomalies: { bloquantes: number; aVerifier: number; info: number };
    fichiers: AdminInitialisationFileDTO[];
    /** Barèmes proposés par la lecture (modifiables avant l'écriture). */
    baremes: AdminBaremeDTO[];
  } | null;
}

/**
 * Un lancement à blanc que la caisse garde (une semaine, déploiements compris) :
 * de quoi choisir lequel reprendre, sans le rapport.
 */
export interface AdminInitialisationResumeDTO {
  id: string;
  compte: string;
  statut: "EN_COURS" | "TERMINE" | "ECHEC";
  creeLe: string;
  termineLe: string | null;
  erreur: string | null;
  magasins: string[];
  ecriture: AdminEcritureResumeDTO | null;
  /** Toutes les écritures de la lecture ; absent d'une caisse antérieure à l'enchaînement. */
  ecritures?: AdminEcritureResumeDTO[];
  ecriturePossible: boolean;
}

export interface AdminEcritureResumeDTO {
  statut: "EN_COURS" | "TERMINE" | "ECHEC";
  magasin: number;
  nomBoutique: string | null;
  interrompue: boolean;
}

/** Une boutique de la caisse Easy Shop, et sa boutique du tableau de bord si elle est liée. */
export interface AdminEasyshopBoutiqueDTO {
  tenantId: string;
  name: string;
  network: { id: string; name: string; slug: string };
  engine: "LOCAL" | "EXTERNAL" | "NONE";
  externalPos: { provider: string; account: string | null; storeId: number | null; disabled: boolean } | null;
  linked: { boutiqueId: string; name: string; city: string; organization: { id: string; name: string }; lastSyncAt: string | null; lastSyncError: string | null } | null;
  /** Boutique du tableau de bord sur le même compte Hiboutik et le même magasin. */
  suggestion: { boutiqueId: string; name: string; city: string; organization: { id: string; name: string } } | null;
}

export interface AdminBoutiqueDTO {
  id: string;
  name: string;
  city: string;
  salesSource: SalesSourceKey;
  easyshopTenantId: string | null;
  organization: { id: string; name: string };
}

export interface AdminEcritureDTO {
  statut: "EN_COURS" | "TERMINE" | "ECHEC";
  /**
   * Ce que la caisse reprend encore après l'écriture rendue (soldes de
   * fidélité, puis photos du catalogue) : chaque partie ajoute son étape au
   * résultat en finissant. Absent d'une caisse antérieure à cette suite.
   */
  suite?: "EN_COURS" | "TERMINE" | null;
  /** Coupée par un redémarrage de la caisse : ce magasin n'est plus proposé depuis cette lecture. */
  interrompue?: boolean;
  demande: {
    magasin: number;
    reseau: { id: string } | { nouveau: string };
    proprietaire: { email: string; nom?: string | null };
    nomBoutique?: string | null;
    reprendreSoldes?: boolean | null;
    clientsReseau?: boolean | null;
  };
  journal: string[];
  erreur: string | null;
  resultat: {
    tenantId: string;
    networkId: string;
    nomBoutique: string;
    proprietaire: { email: string; cree: boolean; lienMotDePasse: string | null };
    etapes: { etape: string; statut: "FAIT" | "ECHEC" | "SAUTE"; detail: string }[];
    /** Absent d'une écriture faite par une caisse antérieure au récapitulatif. */
    recapitulatif?: AdminRecapitulatifDTO | null;
  } | null;
}

/**
 * Ce que la caisse remet au gérant à la fin d'une écriture : chiffres clés,
 * PIN de chaque accès et code du premier poste. Les PIN et le code ne sont
 * lisibles qu'ici et dans le PDF : la caisse ne les garde qu'en empreinte.
 */
export interface AdminRecapitulatifDTO {
  genereLe: string;
  chiffres: {
    clients: number;
    clientsReseau: number;
    produitsActifs: number;
    produits: number;
    referencesEnStock: number;
    /** Centimes HT, au coût d'achat moyen pondéré. */
    stockAchatHt: number;
    /** Centimes TTC, quantité × prix de vente. */
    stockVenteTtc: number;
    vendeurs: number;
  };
  utilisateurs: {
    nom: string;
    role: "Propriétaire" | "Vendeur";
    email: string | null;
    /** En clair, et sans échéance. Absent quand la personne garde son code habituel (`pinHabituel`). */
    pin: string | null;
    /** Vrai : la personne garde le code qu'elle a déjà dans une autre boutique du réseau (haché, donc inconnu ici). */
    pinHabituel?: boolean;
    remarque: string | null;
  }[];
  /** `expireLe` à `null` : sans échéance (le code reste à usage unique). */
  caisse: { nom: string; poste: string; code: string; expireLe: string | null } | null;
  /** Absent d'un récapitulatif rendu par une caisse plus ancienne. */
  acces?: {
    backOffice: string | null;
    /** Récapitulatifs anciens seulement : la caisse n'a plus d'adresse propre, le PDF ne l'affiche plus. */
    caisse?: string | null;
    identifiant: string;
    /**
     * Lien par lequel la propriétaire choisit son mot de passe (usage unique, 30 jours au moins).
     * `null` : elle a déjà un mot de passe. Absent : récapitulatif ancien (consigne « Mot de passe oublié »).
     */
    lienMotDePasse?: string | null;
  };
  /** Informations légales restées vides à la création. */
  aCompleter?: string[];
}

/** Un réseau de la caisse Easy Shop. */
export interface AdminEasyshopReseauDTO {
  id: string;
  name: string;
  slug: string;
  boutiques: number;
}

/** Un barème du vrac : seuils en grammes, prix TTC au gramme en centimes, produits par identifiant Hiboutik. */
export interface AdminBaremeDTO {
  id: string;
  nom: string;
  /** Unité des seuils (absente : gramme). */
  unite?: "GRAMME" | "PIECE";
  /** Saisie des paliers : prix à l'unité, remise en %, prix du palier entier (absente : prix à l'unité). */
  mode?: "UNIT_PRICE" | "DISCOUNT" | "PACKET_PRICE";
  /** Toute la quantité au prix du palier, ou chaque palier est un paquet (absente : toute la quantité). */
  application?: "ALL_UNITS" | "PACKET";
  /** Prix TTC en centimes (modes prix) ou remise en points de base (mode remise). */
  paliers: { seuil: number; prixCentimes?: number | null; remiseBp?: number | null }[];
  produits: number[];
}


// --- Page IT : suivi des livraisons ---

export const IT_STATUSES = ["PLANNED", "IN_PROGRESS", "DONE", "LIVE", "BLOCKED"] as const;
export type ItStatusKey = (typeof IT_STATUSES)[number];
/** Mots simples, pour des gens qui ne sont pas développeurs. */
export const IT_STATUS_LABELS: Record<ItStatusKey, string> = {
  PLANNED: "Prévu",
  IN_PROGRESS: "En cours",
  DONE: "Terminé",
  LIVE: "En ligne",
  BLOCKED: "Bloqué",
};

export interface ItProjectDTO {
  id: string;
  name: string;
  repo: string | null;
  track: boolean;
  colorIndex: number;
  position: number;
  lastSyncedAt: string | null;
}

export interface ItEntryDTO {
  id: string;
  projectId: string;
  title: string;
  client: string | null;
  items: string[];
  /** AAAA-MM-JJ. */
  startDate: string;
  endDate: string | null;
  status: ItStatusKey;
  note: string | null;
  estimateMin: number;
  source: "GITHUB" | "MANUAL";
}

export interface ItOverviewDTO {
  projects: ItProjectDTO[];
  entries: ItEntryDTO[];
  lastSyncAt: string | null;
  /** Un jeton GitHub est enregistré pour ce réseau. */
  githubConnected: boolean;
}

/** Réglages du réseau (permission `network.customize`) : jamais le jeton lui-même. */
export interface ItSettingsDTO {
  enabled: boolean;
  tokenSet: boolean;
  /** Quatre derniers caractères du jeton enregistré. */
  tokenHint: string | null;
  lastSyncAt: string | null;
}

export interface ItSyncResultDTO {
  added: number;
  updated: number;
  /** Dépôts qui n'ont pas pu être lus, avec la raison. */
  errors: { project: string; message: string }[];
  syncedAt: string;
  /** Ce qui a été lu, projet par projet : « 0 commit » se distingue ainsi de « rien lu ». */
  projects: { name: string; repo: string; commits: number; added: number; updated: number }[];
  /** Projets ignorés : sans dépôt, ou dont le suivi est coupé. */
  skipped: string[];
  /** Jour de l'action la plus récente créée ou complétée (pour y amener le calendrier). */
  latestDay: string | null;
}

/** Clé d'accès d'un assistant (MCP) : jamais la clé elle-même, seulement son début. */
export interface ItApiKeyDTO {
  id: string;
  name: string;
  prefix: string;
  canWrite: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}
